import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const steps = [
    { num: '01', ...t.howItWorks.step1 },
    { num: '02', ...t.howItWorks.step2 },
    { num: '03', ...t.howItWorks.step3 },
  ];

  return (
    <section 
      id="how-it-works" 
      ref={sectionRef}
      className="py-32 md:py-40 bg-soft-black relative"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-24 animate-on-scroll">
          <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500 mb-4">
            {t.howItWorks.overline}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-white">
            {t.howItWorks.title}
          </h2>
          <div className="w-16 h-px bg-white/20 mx-auto mt-8" />
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {steps.map((step, index) => (
            <div 
              key={step.num}
              className="animate-on-scroll"
              style={{ transitionDelay: `${index * 0.15}s` }}
            >
              <div className="relative card-premium p-10 md:p-12 min-h-[280px] group">
                {/* Large Background Number */}
                <span className="step-number">{step.num}</span>
                
                {/* Content */}
                <div className="relative z-10">
                  <span className="inline-block text-white/30 text-sm font-medium mb-6 tracking-[0.2em]">
                    {step.num}
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl font-normal text-white mb-5">
                    {step.title}
                  </h3>
                  <p className="text-zinc-500 text-sm leading-relaxed font-light">
                    {step.description}
                  </p>
                </div>

                {/* Bottom line accent */}
                <div className="absolute bottom-0 left-0 w-full h-px bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            </div>
          ))}
        </div>

        {/* Connecting lines between steps - desktop only */}
        <div className="hidden md:flex justify-center items-center gap-4 mt-12">
          <div className="w-24 h-px bg-white/10" />
          <div className="w-2 h-2 border border-white/20 rotate-45" />
          <div className="w-24 h-px bg-white/10" />
          <div className="w-2 h-2 border border-white/20 rotate-45" />
          <div className="w-24 h-px bg-white/10" />
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
